import { resolveShopApiBase, shopApiBases } from '~/utils/shopApi'
import { resolveShopSlugFromRoute } from '~/utils/resolveShopSlug'
import { normalizeStorefrontPath } from '~/utils/storefrontPath'

const RESERVED = new Set([
  'platform',
  'api',
  'uploads',
  '_nuxt',
])

export function useShopSlug() {
  const route = useRoute()
  const { domainShopSlug } = useShopDomain()

  return computed(() =>
    resolveShopSlugFromRoute(
      route.path,
      route.params as Record<string, string | string[]>,
      domainShopSlug.value,
    ),
  )
}

/**
 * Shop-prefixed path from the route passed into middleware.
 * Avoids useRoute(), which Nuxt warns about inside middleware.
 */
export function shopPathForRoute(
  to: { path: string, params?: Record<string, string | string[] | undefined | null> },
  path: string,
): string {
  const domainSlug = useState<{ shop: { slug?: string } | null }>('shop-domain').value?.shop?.slug || ''
  const slug = resolveShopSlugFromRoute(to.path, to.params || {}, domainSlug)
  const shouldPrefix = Boolean(slug && !RESERVED.has(slug) && !domainSlug)
  const normalized = path.startsWith('/') ? path : `/${path}`
  if (!shouldPrefix) return normalized
  if (normalized === `/${slug}` || normalized.startsWith(`/${slug}/`)) return normalized
  return `/${slug}${normalized}`
}

/**
 * Prefix storefront/admin paths with the shop slug on path-based hosts
 * (`/default/produse`). On custom domains the public URL is already bare
 * (`/produse`); the server rewrites to `/{slug}/…` internally.
 *
 * Do not prefix on custom domains: client navigation to `/{slug}/produse/…`
 * is immediately stripped by middleware, and `replace: true` removes the
 * previous page from history (Back skips the listing).
 */
export function useShopPath() {
  const slug = useShopSlug()
  const config = useRuntimeConfig()
  const { customDomainMode } = useShopDomain()

  return (path = '/'): string => {
    const shouldPrefix = Boolean(
      slug.value
      && !RESERVED.has(slug.value)
      && !customDomainMode.value,
    )

    if (!path) {
      return shouldPrefix ? `/${slug.value}` : '/'
    }

    // Absolute external / protocol-relative / mailto / tel — leave unchanged
    // (same-origin http(s) is reduced to a path by normalizeStorefrontPath)
    if (/^(mailto:|tel:|\/\/)/i.test(path)) {
      return path
    }

    const siteOrigin = String(config.public.siteUrl || '').replace(/\/$/, '')
    const normalizedFull = normalizeStorefrontPath(path, siteOrigin)

    if (/^https?:\/\//i.test(normalizedFull)) {
      return normalizedFull
    }

    const queryHashMatch = normalizedFull.match(/[?#].*$/)
    const suffix = queryHashMatch?.[0] || ''
    const pathname = suffix
      ? normalizedFull.slice(0, normalizedFull.length - suffix.length)
      : normalizedFull

    const normalized = !pathname || pathname === '/'
      ? '/'
      : pathname.startsWith('/')
        ? pathname
        : `/${pathname}`

    if (!shouldPrefix) {
      return `${normalized === '/' ? '/' : normalized}${suffix}`
    }

    // Already prefixed with this shop
    if (
      normalized === `/${slug.value}`
      || normalized.startsWith(`/${slug.value}/`)
    ) {
      return `${normalized}${suffix}`
    }

    if (normalized === '/') {
      return `/${slug.value}${suffix}`
    }

    return `/${slug.value}${normalized}${suffix}`
  }
}

export function useShopApiBase() {
  const slug = useShopSlug()
  const config = useRuntimeConfig()
  const { customDomainMode } = useShopDomain()

  return computed(() =>
    resolveShopApiBase(slug.value, {
      apiOrigin: String(config.public.apiOrigin || ''),
      platformApiUrl: String(config.public.platformApiUrl || ''),
      // Custom domain browser calls: same-origin /api (first-party cookies).
      sameOriginApi: customDomainMode.value && import.meta.client,
    }),
  )
}

export function useShopApiBases() {
  const slug = useShopSlug()
  const config = useRuntimeConfig()
  const { customDomainMode } = useShopDomain()

  return () => shopApiBases(slug.value, {
    apiOrigin: String(config.public.apiOrigin || ''),
    platformApiUrl: String(config.public.platformApiUrl || ''),
    sameOriginApi: customDomainMode.value && import.meta.client,
    server: import.meta.server,
  })
}
